import { useCallback, useEffect, useState } from 'react';
import {
  BROWSE_CANDIDATES_PER_TYPE,
  BROWSE_PAGE_SIZE_TYPE,
  type BrowseResultType,
} from 'fym-shared/src/music24/browseSearch';
import { hasBrowseFilter, useBrowseUrlState } from './useBrowseUrlState';
import { isSearchableQuery, useBrowseSearch } from './useBrowseSearch';
import { useBrowseUsage } from './useBrowseUsage';
import { useRecentSearches } from './useRecentSearches';
import { CLEARED_FILTERS } from '../search/BrowseFilters';
import { BROWSE_CREDITS_PANEL_ID } from '../rail/BrowseCreditsPanel';

const PHONE_QUERY = '(max-width: 767.98px)';

function isPhoneWidth(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches
  );
}

/** The state and the actions of the Browse page (design browse-v2), so the page only lays it out. */
export function useBrowsePage() {
  const { state, update } = useBrowseUrlState();
  const [limit, setLimit] = useState(BROWSE_PAGE_SIZE_TYPE);
  const search = useBrowseSearch(state, limit);
  const usage = useBrowseUsage({ refetchOnMount: 'always' });
  const recent = useRecentSearches();
  const addRecent = recent.add;
  const [pasteOpen, setPasteOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [creditsSheetOpen, setCreditsSheetOpen] = useState(false);

  // A new search or a new filter starts at the first page again.
  useEffect(() => {
    setLimit(BROWSE_PAGE_SIZE_TYPE);
  }, [state.query, state.type, state.sort, state.genre, state.countryCode, state.service, state.hideFollowed]);

  const onSearch = useCallback(
    (query: string) => {
      update({ query });
      addRecent(query);
    },
    [update, addRecent]
  );

  const onSubmit = useCallback((query: string) => addRecent(query), [addRecent]);
  const onOpenRow = useCallback(() => addRecent(state.query), [addRecent, state.query]);
  const onTypeChange = useCallback(
    (type: BrowseResultType) => update({ type: type === 'all' ? undefined : type }),
    [update]
  );
  const onShowMore = useCallback(
    () => setLimit((current) => Math.min(current + BROWSE_PAGE_SIZE_TYPE, BROWSE_CANDIDATES_PER_TYPE)),
    []
  );
  const onClearFilters = useCallback(() => update(CLEARED_FILTERS), [update]);

  // design b5 note: a row with no credit left does not open; the click shows the credits.
  const onBlockedOpen = useCallback(() => {
    if (isPhoneWidth()) {
      setCreditsSheetOpen(true);
      return;
    }
    const panel = document.getElementById(BROWSE_CREDITS_PANEL_ID);
    panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    panel?.focus({ preventScroll: true });
  }, []);

  const typeCount = search.data === undefined ? null : search.data.counts[state.type === 'all' ? 'all' : state.type];

  return {
    state,
    update,
    search,
    usage: usage.data,
    recent,
    hasQuery: isSearchableQuery(state.query),
    filterOn: hasBrowseFilter(state),
    typeCount,
    pasteOpen,
    setPasteOpen,
    filterSheetOpen,
    setFilterSheetOpen,
    creditsSheetOpen,
    setCreditsSheetOpen,
    onSearch,
    onSubmit,
    onOpenRow,
    onTypeChange,
    onShowMore,
    onClearFilters,
    onBlockedOpen,
  };
}
